import * as React from "react";
import { format, parse, isValid, setMonth, setYear, getYear, getMonth } from "date-fns";
import { pt, enUS, es, fr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MONTHS_BY_LOCALE = {
  pt: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
  es: ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"],
  fr: ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"]
};

const LOCALES = { pt, en: enUS, es, fr };

// Generate years from 2000 to current year + 5
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2000 + 6 }, (_, i) => 2000 + i);

function DatePicker({
  value,
  onChange,
  placeholder,
  disabled,
  minDate,
  maxDate,
  className,
  "data-testid": testId,
}) {
  const { i18n, t } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const locale = LOCALES[currentLang] || pt;
  const MONTHS = MONTHS_BY_LOCALE[currentLang] || MONTHS_BY_LOCALE.pt;
  
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [displayMonth, setDisplayMonth] = React.useState(value || new Date());

  // Update input value when date changes
  React.useEffect(() => {
    if (value) {
      setInputValue(format(value, "dd/MM/yyyy"));
      setDisplayMonth(value);
    } else {
      setInputValue("");
    }
  }, [value]);

  // When opening, set display month to selected date or today
  React.useEffect(() => {
    if (open) {
      setDisplayMonth(value || new Date());
    }
  }, [open, value]);

  // Handle direct input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Try to parse the date (dd/MM/yyyy format)
    if (newValue.length === 10) {
      const parsed = parse(newValue, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) {
        // Check min date constraint
        if (minDate && parsed < minDate) {
          return;
        }
        onChange(parsed);
        setDisplayMonth(parsed);
      }
    }
  };

  // Handle blur - validate and format
  const handleInputBlur = () => {
    if (inputValue && inputValue.length > 0) {
      const parsed = parse(inputValue, "dd/MM/yyyy", new Date());
      if (isValid(parsed) && (!minDate || parsed >= minDate)) {
        setInputValue(format(parsed, "dd/MM/yyyy"));
      } else if (value) {
        setInputValue(format(value, "dd/MM/yyyy"));
      } else {
        setInputValue("");
      }
    }
  };

  // Handle calendar select
  const handleSelect = (date) => {
    if (date) {
      onChange(date);
      setInputValue(format(date, "dd/MM/yyyy"));
      setDisplayMonth(date);
    }
    // Always close — if date is undefined (re-click on selected), keep current value
    setOpen(false);
  };

  // Handle month change from dropdown
  const handleMonthChange = (monthStr) => {
    const newMonth = parseInt(monthStr, 10);
    setDisplayMonth(setMonth(displayMonth, newMonth));
  };

  // Handle year change from dropdown
  const handleYearChange = (yearStr) => {
    const newYear = parseInt(yearStr, 10);
    setDisplayMonth(setYear(displayMonth, newYear));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal bg-background border border-white/20 hover:border-primary focus:border-primary",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
          data-testid={testId}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "dd/MM/yyyy") : (placeholder || t('date_picker.choose_date'))}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b border-border space-y-2">
          {/* Direct date input */}
          <div>
            <Input
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder="dd/mm/aaaa"
              className="text-center font-mono"
              maxLength={10}
            />
          </div>
          
          {/* Month/Year dropdowns */}
          <div className="flex gap-2">
            <Select
              value={getMonth(displayMonth).toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="flex-1 bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={getYear(displayMonth).toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-24 bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          disabled={(date) => {
            if (minDate && date < minDate) return true;
            if (maxDate && date > maxDate) return true;
            return false;
          }}
          locale={locale}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
